import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { slideAction, saveMirrorToCloudinary } from '../../../../store/slideSlice';
import { slideAPI } from '../../../../store/api';
import { emitSlideAdd } from '../../../../Socket/Socket';
import { getSnapshot } from '../../../../utils/utils';
import { getUpdatedSlides } from '../../../../utils/helper';
import { clamp } from './geometry/primitives';

const SIZE = 8;

/**
 * PowerPoint's yellow diamond adjust handles.
 *
 * Rendered as an absolutely-positioned sibling of the geometry rather than
 * through use-resizer, which is shared with images, textboxes, WordArt and
 * SmartArt and is deliberately left untouched by this rebuild.
 */
const ShapeAdjustHandles = ({ shapeId, preset, adjustments, box, currentSlideId }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const currentSlideData = useSelector((state) => state.slide.slides[currentSlideId]);
  const existingSlides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);
  const committedRef = useRef(null);

  const handles = preset.handles(box, adjustments);

  const onMouseDown = useCallback((event, handle) => {
    // Stop the drag reaching use-resizer, which would move the whole shape.
    event.stopPropagation();
    event.preventDefault();

    const surface = event.currentTarget.parentElement;
    const rect = surface.getBoundingClientRect();
    committedRef.current = null;

    const move = (moveEvent) => {
      const px = moveEvent.clientX - rect.left;
      const py = moveEvent.clientY - rect.top;
      const next = { ...adjustments };

      [handle.adjustX, handle.adjustY].forEach((name) => {
        if (!name) return;
        const spec = preset.adjustments.find((a) => a.name === name);
        next[name] = clamp(spec.fromPoint(box, px, py), spec.min, spec.max);
      });

      const slideData = {
        ...currentSlideData,
        additionalData: {
          ...currentSlideData.additionalData,
          elementsData: {
            ...currentSlideData.additionalData?.elementsData,
            [shapeId]: {
              properties: {
                ...currentSlideData.additionalData?.elementsData?.[shapeId]?.properties,
                adjustments: next,
              },
            },
          },
        },
      };

      // Redux-local only while dragging: committing here would fire an API
      // call and a mirror upload on every mousemove.
      committedRef.current = slideData;
      dispatch(slideAction.addSlide({ slideId: currentSlideId, slideData }));
    };

    const up = async () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const slideData = committedRef.current;
      if (!slideData) return;
      committedRef.current = null;

      const dataUrl = await getSnapshot();
      dispatch(saveMirrorToCloudinary({
        dataId: currentSlideId,
        mirrorSlideDataUrl: dataUrl,
        presentationId: id,
      }));
      emitSlideAdd({
        roomId: id,
        mirrorData: { dataId: currentSlideId, mirrorSlideDataUrl: dataUrl },
        slideData: { slideId: currentSlideId, slideData },
      });
      const updatedSlides = getUpdatedSlides(slideData, 'slide', existingSlides);
      await slideAPI.addNewSlideInDatabase(
        id, currentSlideId, slideData, dispatch,
        { slides: updatedSlides, mirrorSlides },
      );
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [
    adjustments, box, dispatch, preset, shapeId, currentSlideId,
    currentSlideData, existingSlides, mirrorSlides, id,
  ]);

  if (handles.length === 0) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {handles.map((handle, i) => (
        <div
          key={`${shapeId}-adj-${handle.adjustX || ''}${handle.adjustY || ''}-${i}`}
          onMouseDown={(e) => onMouseDown(e, handle)}
          style={{
            position: 'absolute',
            left: handle.x - SIZE / 2,
            top: handle.y - SIZE / 2,
            width: SIZE,
            height: SIZE,
            background: '#ffc000',
            border: '1px solid #7f6000',
            transform: 'rotate(45deg)',
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
        />
      ))}
    </div>
  );
};

export default ShapeAdjustHandles;
